import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonAi } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  chatId: string
  changes: Map<
    string,
    Array<{
      prop: string
      originalValue: unknown
      value: unknown
    }>
  >
  /** undefined unless changed by the AI. */
  lessonTitle: string | undefined
  originalLessonTitle: string | null
  planbookId: string
}

export function prepareLessonAiChange(payload: Payload): PreparedAction {
  return {
    name: "LESSON_AI_CHANGE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  const patches: PatchModel[] = []

  for (const [cardId, propChanges] of payload.changes) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: cardId,
    }).inc("embedded", `attributes._revision`, 1)
    for (const { prop, value } of propChanges) {
      patch = patch.set("embedded", `attributes.${prop}`, value)
    }

    patches.push(patch.patch)
  }

  if (payload.lessonTitle !== undefined) {
    let patch = Patch.create("card-stack", payload.cardStackId).set(
      "attributes.title",
      payload.lessonTitle
    ).patch
    let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStackId).set(
      "attributes.title",
      payload.lessonTitle
    ).patch

    patches.push(patch, patchForCardStackSummary)
  }

  return patches
}

function undoPatches(payload: Payload): Array<PatchModel> {
  const patches: PatchModel[] = []

  for (const [cardId, propChanges] of payload.changes) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: cardId,
    }).inc("embedded", `attributes._revision`, -1)
    for (const { prop, originalValue } of propChanges) {
      patch = patch.set("embedded", `attributes.${prop}`, originalValue)
    }

    patches.push(patch.patch)
  }

  if (payload.lessonTitle !== undefined) {
    let patch = Patch.create("card-stack", payload.cardStackId).set(
      "attributes.title",
      payload.originalLessonTitle
    ).patch
    let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStackId).set(
      "attributes.title",
      payload.originalLessonTitle
    ).patch

    patches.push(patch, patchForCardStackSummary)
  }

  return patches
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let models = defaultLessonModels(payload, findFn, userId)
  let primaryPossessive = getPossessiveForPlanbook(findFn, userId, models.planbook?.id)
  const context = defaultLessonContext(payload, models)
  let newContext = assign({}, context, { chatId: payload.chatId })
  const curLessonTitle =
    payload.lessonTitle !== undefined ? payload.lessonTitle : payload.originalLessonTitle
  const lessonTitleDesc = curLessonTitle
    ? `"${curLessonTitle}"`
    : `your ${context.courseTitle} lesson on ${context.date}`

  let newEvent: LessonAi = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,

    primary_verb: "use",
    primary_possessive: primaryPossessive,
    primary_object_type: "lesson",
    primary_object_specifier: "ai",

    adverbial_verb: "accepting",
    adverbial_object_type: "chat",
    adverbial_object_specifier: null,
    adverbial_object_id: payload.chatId,
    adverbial_object_id_and_ancestor_ids: [...models.cardStackIdAndAncestorIds, payload.chatId],
  }

  return {
    title: "Changed with AI",
    titleForAnalytics: "Lesson - AI Accept Changes",
    description: `Accepted AI changes to ${lessonTitleDesc}`,
    activity: defaultLessonActivity(payload, models),
    context: newContext,
    newAnalyticsEvents: [newEvent],
  }
}
